/**
 * The top-right corner: who you are, and the two places that are about you
 * rather than about money.
 *
 * WHY A POPOVER AND NOT RAIL ITEMS. Profile and Household are both settings
 * about *people* — visited rarely, and never while you are in the middle of
 * recording a shop. The left rail is for places you go to do the thing the app
 * is for, and every item added to it makes the four that matter slightly harder
 * to hit. Folding these behind one avatar is the standard shape because it
 * works: one target, always in the same place, costing one line of chrome.
 *
 * THE SWITCHER LIVES HERE TOO, above the links, because "which household" is
 * the same kind of question as "who am I" — and because the avatar is then the
 * one place you look when the numbers on screen are not the ones you expected.
 */
import { useEffect, useRef, useState } from 'react';

export interface MenuHousehold {
  householdId: string;
  name: string;
  memberId: string;
  role: string;
}

export function AccountMenu({
  account,
  avatar,
  image,
  currentHouseholdId,
  households,
  onSwitch,
  onGo,
  onSignOut,
}: {
  account: { name: string; email: string };
  /** The emoji they chose, if any. Beats the provider photo. */
  avatar: string | null;
  /** A photo URL from an OAuth provider, if any. */
  image: string | null;
  currentHouseholdId: string;
  households: MenuHousehold[];
  onSwitch: (to: { householdId: string; memberId: string }) => void;
  onGo: (to: 'profile' | 'household') => void;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLDivElement | null>(null);

  // Close on a click anywhere else and on Escape. Both, because a popover that
  // only closes one way is one people learn to distrust.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!anchor.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const here = households.find((h) => h.householdId === currentHouseholdId);

  return (
    <div className="account-menu" ref={anchor}>
      <button
        type="button"
        className="account-btn"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Account — ${account.name || account.email}`}
        title={account.name || account.email}
      >
        <Avatar account={account} avatar={avatar} image={image} />
      </button>

      {open && (
        <div className="popover account-pop" role="dialog" aria-label="Account">
          <div className="account-pop-head">
            <Avatar account={account} avatar={avatar} image={image} large />
            <div className="account-pop-who">
              <strong>{account.name || 'Unnamed'}</strong>
              <span className="hint">{account.email}</span>
            </div>
          </div>

          {households.length > 0 && (
            <label className="account-pop-switch">
              <span className="field-label">Household</span>
              <select
                value={currentHouseholdId}
                aria-label="Household"
                disabled={households.length < 2}
                onChange={(e) => {
                  const next = households.find((h) => h.householdId === e.target.value);
                  if (next) onSwitch({ householdId: next.householdId, memberId: next.memberId });
                }}
              >
                {households.map((h) => (
                  <option key={h.householdId} value={h.householdId}>
                    {h.name}
                    {h.role === 'OWNER' ? '' : ` · ${h.role.toLowerCase()}`}
                  </option>
                ))}
              </select>
              {/* With one household the select is inert, so say what it is for
                  rather than leaving a dead control with no explanation. */}
              {households.length < 2 && (
                <span className="hint">Your only household. Make another from Household.</span>
              )}
            </label>
          )}

          <div className="account-pop-links">
            <button type="button" onClick={() => { setOpen(false); onGo('profile'); }}>
              Your profile
            </button>
            <button type="button" onClick={() => { setOpen(false); onGo('household'); }}>
              Household{here ? ` · ${here.name}` : ''}
            </button>
          </div>

          <div className="account-pop-foot">
            <button type="button" className="chip chip-ghost" onClick={onSignOut}>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Emoji, else provider photo, else initial — and never nothing.
 *
 * An empty circle reads as an avatar that failed to load, which makes the app
 * look broken rather than unconfigured. The initial is also genuinely useful
 * the moment two people share a laptop.
 */
function Avatar({
  account,
  avatar,
  image,
  large,
}: {
  account: { name: string; email: string };
  avatar: string | null;
  image: string | null;
  large?: boolean;
}) {
  const className = large ? 'avatar avatar-lg' : 'avatar';

  if (avatar) {
    return (
      <span className={className} aria-hidden="true">
        {avatar}
      </span>
    );
  }
  if (image) {
    return <img className={className} src={image} alt="" aria-hidden="true" />;
  }
  return (
    <span className={`${className} is-initial`} aria-hidden="true">
      {initialOf(account.name || account.email)}
    </span>
  );
}

/** By code point — `charAt(0)` splits an emoji and renders a replacement box. */
function initialOf(who: string): string {
  const first = [...who.trim()][0] ?? '?';
  return first.toLocaleUpperCase();
}
